// Family Principal Portal — Phase 1 redesign
// 9 enterprise-grade sections, each respecting per-user visibility flags.
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, LogOut, ArrowLeft } from 'lucide-react';
import { portalApi, portalAuth } from '@/lib/portalApi';
import { useFocusItem } from '@/hooks/useFocusItem';
import {
  WelcomeSection,
  WealthSnapshotSection,
  ActionItemsSection,
  DocumentRequestsSection,
  SharedDocumentsSection,
  YourTeamSection,
  WhatsChangedSection,
  FamilyEntitySection,
  MessagesSection,
} from '@/components/portal/FamilyPortalSections';

export default function FamilyPortal() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [client, setClient] = useState(null);
  const [individuals, setIndividuals] = useState([]);
  const [entities, setEntities] = useState([]);

  // Section data
  const [wealth, setWealth] = useState(null);
  const [team, setTeam] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [sharedDocs, setSharedDocs] = useState([]);
  const [whatsChanged, setWhatsChanged] = useState([]);
  const [messages, setMessages] = useState([]);

  const [loading, setLoading] = useState(true);
  useFocusItem(!loading);
  const [posting, setPosting] = useState(false);

  const cachedUser = portalAuth.getUser();

  const loadAll = useCallback(async (clientId, visibility) => {
    // Run all section fetches in parallel but tolerate per-section failures
    // (e.g. 403 if an admin has toggled a section off — we just hide it).
    const calls = [
      visibility.wealth_snapshot && portalApi.wealthSnapshot(clientId).then((r) => setWealth(r.data)).catch(() => {}),
      visibility.team && portalApi.team(clientId).then((r) => setTeam(r.data?.members || [])).catch(() => {}),
      visibility.action_items && portalApi.actionItems(clientId).then((r) => setActionItems(r.data?.items || [])).catch(() => {}),
      visibility.document_requests && portalApi.documentRequests(clientId).then((r) => setDocRequests(r.data?.items || [])).catch(() => {}),
      visibility.documents && portalApi.sharedDocuments(clientId).then((r) => setSharedDocs(r.data?.items || [])).catch(() => {}),
      visibility.whats_changed && portalApi.whatsChanged(clientId).then((r) => setWhatsChanged(r.data?.items || [])).catch(() => {}),
      visibility.messages && portalApi.messages(clientId).then((r) => setMessages(r.data?.items || [])).catch(() => {}),
    ].filter(Boolean);
    await Promise.all(calls);
  }, []);

  useEffect(() => {
    if (!portalAuth.getToken()) {
      navigate('/portal/login', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Pull the enriched 'me' (gives us visibility + welcome note)
        const meRes = await portalApi.me();
        if (cancelled) return;
        setMe(meRes.data);

        // Pick the first client they have access to
        const lst = await portalApi.listClients();
        const clients = lst.data?.clients || [];
        if (!clients.length) {
          setLoading(false);
          return;
        }
        const det = await portalApi.getClient(clients[0].id);
        if (cancelled) return;
        setClient(det.data?.client);
        setIndividuals(det.data?.individuals || []);
        setEntities(det.data?.entities || []);

        await loadAll(clients[0].id, meRes.data?.visibility || {});
      } catch (e) {
        toast.error('Unable to load your portal');
        navigate('/portal/login', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, loadAll]);

  const logout = () => {
    portalAuth.clear();
    navigate('/portal/login', { replace: true });
  };

  const postMessage = async (text) => {
    if (!client?.id) return;
    setPosting(true);
    try {
      const res = await portalApi.postMessage(client.id, text);
      setMessages((prev) => [res.data?.entry, ...prev]);
    } catch (e) {
      toast.error('Could not send message');
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F5]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F5] p-8">
        <p className="text-[#5D524D]">No portal access has been configured. Please contact your adviser.</p>
      </div>
    );
  }

  const vis = me?.visibility || {};
  const leadAdviser = team.find((m) => m.isLead);

  return (
    <div className="min-h-screen bg-[#F9F7F5]" data-testid="family-portal">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[#E6E0D9]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-3 flex items-center justify-between">
          <a href="/" className="inline-flex items-center gap-2 text-xs text-[#8C8078] hover:text-[#2C2420]" data-testid="portal-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to QIndex One
          </a>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#8C8078] hidden sm:inline">{cachedUser?.email}</span>
            <button onClick={logout} className="text-sm text-[#5D524D] hover:text-[#2C2420] inline-flex items-center gap-1.5" data-testid="portal-logout-btn">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 sm:px-8 py-8 space-y-6">
        <WelcomeSection portalUser={me} clientName={client.name} leadAdviser={leadAdviser} />

        {vis.wealth_snapshot && <WealthSnapshotSection data={wealth} />}
        {vis.action_items && <ActionItemsSection items={actionItems} />}
        {vis.document_requests && <DocumentRequestsSection items={docRequests} />}
        {vis.documents && <SharedDocumentsSection items={sharedDocs} />}
        {vis.team && <YourTeamSection members={team} />}
        {vis.whats_changed && <WhatsChangedSection items={whatsChanged} />}
        {vis.family_entity && <FamilyEntitySection individuals={individuals} entities={entities} />}
        {vis.messages && (
          <MessagesSection
            items={messages}
            canPost={(me?.permissions || {}).canComment !== false}
            onPost={postMessage}
            posting={posting}
            currentUserId={me?.id}
          />
        )}
      </main>

      <footer className="max-w-5xl mx-auto px-6 sm:px-8 pb-8 pt-2 text-center">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C8078]">QIndex&nbsp;One · Private &amp; Confidential</p>
      </footer>
    </div>
  );
}
